/* eslint-disable react/jsx-max-props-per-line */
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { Grid, Typography } from '@mui/material';
import moment from 'moment';
import {
  ArquivoContainerV2,
  DatePickerV2,
  DefaultSelectV2,
  DialogDefaultV2,
  InputV2,
  SwitchV2,
} from '../../../../components/v2';
import * as selectorsAuth from '../../../../store/auth/reducer';
import * as selectorsNsmp from '../../../../store/nsmp/reducer';
import * as actionsNsmp from '../../../../store/nsmp/actions';
import * as Role from '../../../../constants/role';
import SucataMateriaisSection from './components/SucataMateriaisSection';
import {
  buildSucataPayload,
  getEmptySucataFormValues,
  isSemSucata,
  mapNsmpToSucataFormValues,
  TIPO_NSMP_SUCATA,
  validatePesagemSucata,
} from '../utils/usoMutuoSucataSchema';
import {
  findPeriodoOptionId,
  getPeriodoFromOptionId,
  resolvePeriodoOptions,
  toSucataTimestamp,
} from '../utils/usoMutuoPeriodo';

const rootGridSx = { pt: 1 };
const fieldContainerSx = { pb: 0 };
const requiredTextSx = { color: 'error.main', pt: 0.5 };

const buildCarouselHeaderActions = ({
  carouselPosition,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  isAdmin,
  readOnlyLocal,
  onEdit,
}) => {
  const actions = [];
  if (carouselPosition) {
    actions.push({ title: '◄', onClick: onPrevious, disabled: !hasPrevious });
    actions.push({
      title: `${carouselPosition.current} / ${carouselPosition.total}`,
      onClick: () => {},
      disabled: true,
    });
    actions.push({ title: '►', onClick: onNext, disabled: !hasNext });
  }
  if (isAdmin && readOnlyLocal) {
    actions.push({ onClick: onEdit, title: 'Editar' });
  }
  return actions.length > 0 ? actions : null;
};

const UsoMutuoPesagemSucataModal = ({
  open,
  onCancel,
  onConfirm,
  selectedId,
  nsmpSelected,
  loadNsmpById,
  readOnly,
  userRole,
  nsirTitle,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
  carouselPosition,
}) => {
  const [canEditNsmp, setCanEditNsmp] = useState(false);
  const [readOnlyLocal, setReadOnlyLocal] = useState(readOnly);
  const [editFiles, setEditFiles] = useState(false);
  const [errors, setErrors] = useState({});
  const [values, setValues] = useState(getEmptySucataFormValues());

  const periodoOptions = useMemo(
    () => resolvePeriodoOptions(values.sucata, values.sucata?.data || moment()),
    [values.sucata]
  );

  useEffect(() => {
    setReadOnlyLocal(readOnly);
  }, [readOnly]);

  useEffect(() => {
    if (selectedId) loadNsmpById(selectedId);
  }, [selectedId, loadNsmpById]);

  useEffect(() => {
    if (!open) return;
    if (nsmpSelected && nsmpSelected.id) {
      const mapped = mapNsmpToSucataFormValues(nsmpSelected);
      const options = resolvePeriodoOptions(mapped.sucata, mapped.sucata.data);
      mapped.periodoOptionId = findPeriodoOptionId(
        mapped.sucata.periodoInicio,
        mapped.sucata.periodoFim,
        options
      );
      setValues(mapped);
    } else if (!selectedId) {
      setValues(getEmptySucataFormValues());
    }
  }, [nsmpSelected, open, selectedId]);

  const clearForm = () => {
    setCanEditNsmp(false);
    setEditFiles(false);
    setErrors({});
    setValues(getEmptySucataFormValues());
  };

  const revertToViewMode = () => {
    setCanEditNsmp(false);
    setEditFiles(false);
    setErrors({});
    setReadOnlyLocal(true);
    if (nsmpSelected && nsmpSelected.id) {
      const mapped = mapNsmpToSucataFormValues(nsmpSelected);
      const options = resolvePeriodoOptions(mapped.sucata, mapped.sucata.data);
      mapped.periodoOptionId = findPeriodoOptionId(
        mapped.sucata.periodoInicio,
        mapped.sucata.periodoFim,
        options
      );
      setValues(mapped);
    }
  };

  const onCancelLocal = () => {
    if (canEditNsmp) {
      revertToViewMode();
      return;
    }
    clearForm();
    onCancel();
  };

  const handleChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSucataChange = (field, value) => {
    setValues((prev) => ({
      ...prev,
      sucata: {
        ...prev.sucata,
        [field]: value,
      },
    }));
  };

  const handleSemSucataToggle = (event) => {
    const checked = event.target.checked;
    setErrors({});
    setValues((prev) => ({
      ...prev,
      sucata: {
        ...prev.sucata,
        semSucata: checked,
        itens: checked ? [] : prev.sucata?.itens,
        pesoTotalKg: checked ? 0 : prev.sucata?.pesoTotalKg,
      },
    }));
  };

  const handlePeriodoSelect = (event) => {
    const periodoOptionId = event.target.value;
    const periodo = getPeriodoFromOptionId(periodoOptionId, periodoOptions);
    setValues((prev) => ({
      ...prev,
      periodoOptionId,
      sucata: {
        ...prev.sucata,
        periodoInicio: periodo?.periodoInicio || null,
        periodoFim: periodo?.periodoFim || null,
      },
    }));
  };

  const onConfirmLocal = () => {
    const validationErrors = validatePesagemSucata(values);
    setErrors(validationErrors || {});
    if (validationErrors) return;

    const periodo = getPeriodoFromOptionId(values.periodoOptionId, periodoOptions);
    const payload = buildSucataPayload({
      values,
      tipoNsmp: TIPO_NSMP_SUCATA.PESAGEM,
      updateFiles: editFiles,
      periodoInicio: periodo?.periodoInicio,
      periodoFim: periodo?.periodoFim,
    });

    clearForm();
    onConfirm(payload);
  };

  const isAdmin = () => userRole === Role.ADMIN || userRole === Role.SUPER_ADMIN;
  const isReadOnly = canEditNsmp ? false : readOnlyLocal;
  const isFieldDisabled = isReadOnly;
  const semSucata = isSemSucata(values.sucata);

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onCancelLocal}
      handlerConfirm={onConfirmLocal}
      title={`${nsirTitle} > Pesagem Sucata`}
      confirmMessage="Salvar"
      maxWidth="md"
      fullWidth
      readOnly={isReadOnly}
      headerAction={buildCarouselHeaderActions({
        carouselPosition,
        onPrevious,
        onNext,
        hasPrevious,
        hasNext,
        isAdmin: isAdmin(),
        readOnlyLocal,
        onEdit: () => {
          setReadOnlyLocal(false);
          setCanEditNsmp(true);
        },
      })}
    >
      <Grid container spacing={1} sx={rootGridSx}>
        <Grid size={{ xs: 12 }} sx={fieldContainerSx}>
          <DatePickerV2
            label="Data da Pesagem"
            isRequired
            value={toSucataTimestamp(values.sucata?.data)}
            error={!!(errors && errors['sucata.data'])}
            onChange={(data) => handleSucataChange('data', data)}
            disabled={isFieldDisabled}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={fieldContainerSx}>
          <DefaultSelectV2
            label="Período"
            isRequired
            showEmpty
            options={periodoOptions}
            value={values.periodoOptionId || ''}
            error={!!(errors && errors.periodoOptionId)}
            onSelect={handlePeriodoSelect}
            disabled={isFieldDisabled}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={fieldContainerSx}>
          <SwitchV2
            label="Sem sucata nesta semana"
            checked={semSucata}
            handleChange={handleSemSucataToggle}
            disabled={isFieldDisabled}
          />
        </Grid>
        {!semSucata && (
          <Grid size={{ xs: 12 }} sx={fieldContainerSx}>
            <SucataMateriaisSection
              itens={values.sucata?.itens || []}
              onChange={(itens) => handleSucataChange('itens', itens)}
              disabled={isFieldDisabled}
              error={errors && errors['sucata.itens'] ? errors['sucata.itens'][0] : null}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }} sx={fieldContainerSx}>
          <InputV2
            label="Observação"
            name="observacao"
            multiline
            rows={4}
            value={values.observacao || ''}
            onChange={(event) => handleChange('observacao', event.target.value)}
            disabled={isFieldDisabled}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={fieldContainerSx}>
          <ArquivoContainerV2
            arquivos={values.fotos}
            filesLimit={1}
            onSave={(fotos) => {
              setEditFiles(true);
              setValues((prev) => ({ ...prev, fotos }));
            }}
            disabled={isFieldDisabled}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={fieldContainerSx}>
          {errors && errors.fotos && (
            <Typography sx={requiredTextSx}>*Imagens são obrigatórias</Typography>
          )}
        </Grid>
      </Grid>
    </DialogDefaultV2>
  );
};

UsoMutuoPesagemSucataModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selectedId: PropTypes.string,
  nsmpSelected: PropTypes.object,
  loadNsmpById: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  userRole: PropTypes.string,
  nsirTitle: PropTypes.string.isRequired,
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  hasNext: PropTypes.bool,
  hasPrevious: PropTypes.bool,
  carouselPosition: PropTypes.object,
};

UsoMutuoPesagemSucataModal.defaultProps = {
  selectedId: undefined,
  nsmpSelected: {},
};

const mapStateToProps = (state) => ({
  nsmpSelected: selectorsNsmp.getNsmpSelected(state),
  userRole: selectorsAuth.getRole(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...actionsNsmp }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(UsoMutuoPesagemSucataModal);
